import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, Row, Col } from 'components';

/**
 * Guards a screen the user reached without the needed permission.
 *
 * The entry point stays visible on purpose: instead of hiding it, the user is
 * told why the screen is closed and is sent back to where they came from once
 * they confirm the modal.
 *
 * Where a section has more than one screen, `redirectTo` names the one the user
 * may actually open: being moved one tab along beats being thrown out of the
 * section over a screen they never asked for.
 *
 * @param {boolean} allowed        whether the user may see the screen
 * @param {string}  fallbackPath   where to go when there is no previous screen
 * @param {string}  messagePrefix  translation keys of the heading and the text
 * @param {?string} redirectTo     a screen of this section the user may open
 */
const useAccessGuard = (
  allowed,
  fallbackPath = '/ma',
  messagePrefix = 'page.ma.meetings.accessDenied',
  redirectTo = null,
) => {
  const history = useHistory();
  const { t } = useTranslation();

  const goBack = () => {
    if (redirectTo) {
      history.push(redirectTo);
      return;
    }

    if (history.length > 1) {
      history.goBack();
      return;
    }

    history.push(fallbackPath);
  };

  const renderAccessDeniedModal = () => (
    <Modal
      heading={t(`${messagePrefix}.heading`)}
      description={t(`${messagePrefix}.description`)}
      isOpen={!allowed}
      confirm={goBack}
      cancel={goBack}
      onRequestClose={goBack}
      customActions={(Action) => (
        <Row>
          <Col size={{ xs: 1 / 1 }}>
            <Action onClick={goBack}>{t('global.confirmButton')}</Action>
          </Col>
        </Row>
      )}
    />
  );

  return [allowed, renderAccessDeniedModal];
};

export default useAccessGuard;
